import {
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutline as CheckCircleIcon,
  EventNote as EventNoteIcon,
  People as PeopleIcon,
  RadioButtonUnchecked as DraftIcon,
} from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDirection } from '../context/DirectionContext';
import { translate as tr, translateOr } from '../i18n/translations';
import apiService from '../services/api';
import { CycleStatus } from '../types/api';
import SubmissionTrackerCard from '../components/Dashboard/SubmissionTrackerCard';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay },
});

const Dashboard: React.FC = () => {
  const { state } = useAuth();
  const navigate = useNavigate();
  const { lang } = useDirection();

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiService.getUsers(),
  });

  const { data: cycles, isLoading: cyclesLoading } = useQuery({
    queryKey: ['cycles'],
    queryFn: () => apiService.getCycles(),
  });

  const loading = usersLoading || cyclesLoading;
  const activeCycle = cycles?.find(c => c.status === CycleStatus.ACTIVE);
  const draftCount = cycles?.filter(c => c.status === CycleStatus.DRAFT).length ?? 0;
  const closedCount = cycles?.filter(c => c.status === CycleStatus.CLOSED).length ?? 0;

  const stats = [
    {
      label: tr('dash.totalUsers', lang),
      value: users?.length ?? 0,
      icon: <PeopleIcon />,
      color: '#6366F1',
      bg: '#EEF2FF',
      onClick: () => navigate('/users'),
    },
    {
      label: tr('dash.draftCycles', lang),
      value: draftCount,
      icon: <DraftIcon />,
      color: '#D97706',
      bg: '#FEF3C7',
      onClick: () => navigate('/cycles'),
    },
    {
      label: tr('dash.closedCycles', lang),
      value: closedCount,
      icon: <CheckCircleIcon />,
      color: '#64748B',
      bg: '#F1F5F9',
      onClick: () => navigate('/cycles'),
    },
    {
      label: tr('dash.totalCycles', lang),
      value: cycles?.length ?? 0,
      icon: <EventNoteIcon />,
      color: '#2563EB',
      bg: '#EFF6FF',
      onClick: () => navigate('/cycles'),
    },
  ];

  const sortedCycles = cycles
    ? [...cycles].sort((a, b) => {
        const order: Record<string, number> = { active: 0, draft: 1, closed: 2 };
        return (order[a.status] ?? 3) - (order[b.status] ?? 3);
      })
    : [];

  return (
    <Box>
      {/* Page heading */}
      <motion.div {...fadeUp()}>
        <Typography
          sx={{
            color: '#64748B',
            fontSize: '0.72rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            mb: 0.5,
          }}
        >
          {tr('dash.section', lang)}
        </Typography>
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          {state.user ? `${tr('dash.greeting', lang)}, ${state.user.firstName}` : tr('dash.section', lang)}
        </Typography>
        <Typography sx={{ color: '#64748B', fontSize: '0.875rem', mb: 4 }}>
          {tr('dash.subtitle', lang)}
        </Typography>
      </motion.div>

      {/* Active cycle spotlight */}
      <motion.div {...fadeUp(0.05)}>
        {loading ? (
          <Skeleton variant="rounded" height={118} sx={{ mb: 3, borderRadius: 3 }} />
        ) : activeCycle ? (
          <Card
            sx={{
              mb: 3,
              background: 'linear-gradient(135deg, #4338CA 0%, #6366F1 60%, #818CF8 100%)',
              border: 'none',
            }}
          >
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box>
                  <Chip
                    label={tr('dash.activeCycle', lang)}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.18)',
                      color: '#fff',
                      fontWeight: 700,
                      mb: 1.5,
                      borderRadius: 2,
                      fontSize: '0.72rem',
                    }}
                  />
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, mb: 0.5 }}>
                    {activeCycle.displayName}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.85rem' }}>
                    {activeCycle.monthYear}
                    {activeCycle.startedAt &&
                      ` · ${tr('dash.started', lang)} ${new Date(activeCycle.startedAt).toLocaleDateString()}`}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate(`/cycles/${activeCycle.id}`)}
                  sx={{
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.4)',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  {tr('dash.viewCycle', lang)}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card
            sx={{
              mb: 3,
              border: '2px dashed #E2E8F0',
              boxShadow: 'none',
              bgcolor: 'transparent',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center', '&:last-child': { pb: 3 } }}>
              <Typography sx={{ color: '#94A3B8', mb: 1.5, fontSize: '0.9rem' }}>
                {tr('dash.noActiveCycle', lang)}
              </Typography>
              <Button variant="contained" size="small" onClick={() => navigate('/cycles')}>
                {tr('dash.manageCycles', lang)}
              </Button>
            </CardContent>
          </Card>
        )}
      </motion.div>

      {/* Form submission tracker — active cycle only */}
      {activeCycle && (
        <motion.div {...fadeUp(0.08)}>
          <SubmissionTrackerCard cycleId={activeCycle.id} />
        </motion.div>
      )}

      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 2,
          mb: 3,
        }}
      >
        {stats.map((stat, i) => (
          <motion.div key={stat.label} {...fadeUp(0.1 + i * 0.04)}>
            {loading ? (
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: 3 }} />
            ) : (
              <Card
                onClick={stat.onClick}
                sx={{ cursor: 'pointer', '&:hover': { borderColor: '#C7D2FE', transform: 'translateY(-1px)', transition: 'all 0.15s' } }}
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      bgcolor: stat.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: stat.color,
                      mb: 1.5,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1, mb: 0.5 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: '#64748B', fontSize: '0.78rem' }}>
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </motion.div>
        ))}
      </Box>

      {/* All cycles list */}
      <motion.div {...fadeUp(0.28)}>
        <Card>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid #F1F5F9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{tr('dash.allCycles', lang)}</Typography>
            <Button
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/cycles')}
              sx={{ color: '#6366F1', fontSize: '0.8rem' }}
            >
              {tr('dash.manage', lang)}
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} height={52} sx={{ mb: 1, borderRadius: 2 }} />
              ))}
            </Box>
          ) : !sortedCycles.length ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                {tr('dash.noCycles', lang)}
              </Typography>
            </Box>
          ) : (
            sortedCycles.map((cycle, i) => (
              <Box
                key={cycle.id}
                onClick={() => navigate(`/cycles/${cycle.id}`)}
                sx={{
                  px: 3,
                  py: 1.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: i < sortedCycles.length - 1 ? '1px solid #F1F5F9' : 'none',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#F8FAFC' },
                  transition: 'background 0.12s',
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0F172A' }}>
                    {cycle.displayName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.775rem', color: '#94A3B8', mt: 0.25 }}>
                    {cycle.monthYear}
                  </Typography>
                </Box>
                <Chip
                  label={translateOr(`cycleStatus.${cycle.status}`, lang, cycle.status)}
                  size="small"
                  color={
                    cycle.status === CycleStatus.ACTIVE
                      ? 'success'
                      : cycle.status === CycleStatus.DRAFT
                        ? 'warning'
                        : 'default'
                  }
                />
              </Box>
            ))
          )}
        </Card>
      </motion.div>
    </Box>
  );
};

export default Dashboard;
